import { Component, Inject, computed } from '@angular/core';

import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideAngularModule, User, Menu, EyeOff, Copy } from 'lucide-angular';
import { MessageListComponent } from '../../chatbot/chat/components/message-list.component';
import { ConversationSession, ConversationVisualizerService } from '../../../core/services/conversation-visualizer.service';
import { Message } from '../../../core/models/message.model';
import { SessionBenchmarkDialogComponent } from './session-benchmark-dialog.component';

export interface SessionDetailModalData {
  session: ConversationSession;
  formatTimestamp: (timestamp: string) => string;
  copyToClipboard: (text: string) => void;
}

@Component({
  selector: 'app-session-detail-modal',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    LucideAngularModule,
    MessageListComponent
],
  templateUrl: './session-detail-modal.component.html',
  styleUrls: ['./session-detail-modal.component.scss']
})
export class SessionDetailModalComponent {
  readonly UserIcon = User;
  readonly EyeIcon = EyeOff;
  readonly MenuIcon = Menu;
  readonly CopyIcon = Copy;

  // State for showing/hiding session details
  // Start with true on desktop (>= 768px), false on mobile
  showDetails: boolean = typeof window !== 'undefined' && window.innerWidth >= 768;

  // Computed - all messages (including discarded)
  allMessages = computed<Message[]>(() => {
    if (!this.data.session || !this.data.session.messages) {
      return [];
    }

    return this.data.session.messages.map(msg => this.conversationService.convertToMessage(msg));
  });

  // Computed - visible messages (excluding DISCARDED bot messages)
  // User DISCARDED messages are shown normally
  messages = computed<Message[]>(() => {
    return this.allMessages().filter(msg => {
      // Show all non-discarded messages
      if (msg.state !== 'DISCARDED') return true;
      // Show discarded user messages normally (they are merged into following user messages)
      if (msg.isUser) return true;
      // Hide discarded bot messages (they'll be shown inline with the discarded-message-inline component)
      return false;
    });
  });

  constructor(
    public dialogRef: MatDialogRef<SessionDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SessionDetailModalData,
    private conversationService: ConversationVisualizerService,
    private dialog: MatDialog
  ) {}

  close(): void {
    this.dialogRef.close();
  }

  formatTimestamp(timestamp: string): string {
    return this.data.formatTimestamp(timestamp);
  }

  copyToClipboard(text: string): void {
    this.data.copyToClipboard(text);
  }

  toggleDetails(): void {
    this.showDetails = !this.showDetails;
  }

  openBenchmarkDialog(): void {
    this.dialog.open(SessionBenchmarkDialogComponent, {
      data: {
        sessionId: this.data.session.sessionId,
        conversationId: this.data.session.conversationId,
        conversationStartedAt: this.data.session.startedAt,
        allMessages: this.allMessages()
      },
      width: '96vw',
      maxWidth: '96vw',
      maxHeight: '92vh',
      panelClass: 'session-benchmark-dialog',
      autoFocus: false
    });
  }
}
